import {
  Check,
  ChevronDown,
  Loader2,
  Plug,
  Plus,
  Search,
  Unplug,
  X,
} from 'lucide-react'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  IntegrationSuggestionSurfaces,
  OnboardingEvents,
  trackEvent,
} from '../analytics/posthog'
import {
  getApiErrorMessage,
  pipedreamAppsService,
} from '../services/api'
import {
  usePipedreamConnections,
} from '../hooks/usePipedreamConnections'
import type {
  PipedreamAppCatalogItem,
  PipedreamAppConnectionResponse,
} from '../client'
import {
  pipedreamAppSuggestion,
  useSuggestionConnect,
} from '../hooks/useSuggestionConnect'
import {
  PipedreamAddAccountModal,
} from './PipedreamAddAccountModal'
import {
  canAddPipedreamAccount,
  groupPipedreamAccounts,
  isDefaultPipedreamAccount,
  pipedreamAccountName,
  pipedreamConnectLanded,
  pipedreamConnectionTitle,
} from '../utils/pipedreamAccounts'

function AppLogo({
  name,
  src,
  size = 'md',
}: {
  name: string
  src?: string | null | undefined
  size?: 'sm' | 'md'
}) {
  const dimensions = size === 'sm' ? 'w-8 h-8 rounded-lg' : 'w-11 h-11 rounded-xl'
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className={`${dimensions} object-contain bg-white border border-gray-100 p-1`}
      />
    )
  }
  return (
    <div
      className={`${dimensions} bg-primary-50 text-primary-700 flex items-center justify-center font-semibold`}
      aria-hidden="true"
    >
      {name.slice(0, 1).toUpperCase()}
    </div>
  )
}

interface PipedreamIntegrationsCardProps {
  // The connection manager must outlive the browse surface: suggestions
  // disappear once connected, while the full catalog is intentionally hidden
  // from most users. When false, this component renders only connected apps
  // (and self-hides when there are none).
  catalogBrowseEnabled?: boolean
  // Lets another card on the page open this modal, optionally pre-searched.
  // Carries a nonce rather than just a string so asking for the same app twice
  // in a row still reopens the modal — a bare string would compare equal and
  // the second click would do nothing.
  openRequest?: { query: string; nonce: number } | null | undefined
  // Opens the "Request an integration" form from the catalog's empty state,
  // seeded with what the user searched for. Without it a search that finds
  // nothing is a dead end (GET-14034) — and a name we cannot match is exactly
  // the request worth filing.
  onRequestIntegration?: ((searchedName: string) => void) | undefined
  // Slugs the page presents as tiles of their own elsewhere. Their connections
  // are still read (the catalog needs them) but not listed twice on the page.
  managedElsewhere?: readonly string[]
}

export function PipedreamIntegrationsCard({
  catalogBrowseEnabled = true,
  openRequest = null,
  onRequestIntegration,
  managedElsewhere = [],
}: PipedreamIntegrationsCardProps = {}) {
  const [open, setOpen] = useState(false)
  // One read for the whole page — the featured category tiles consume the same
  // query key, so a connect on either surface updates both.
  const {
    connections,
    loading: connectionsLoading,
    error: connectionsError,
    refresh: loadConnections,
    disconnect: disconnectRows,
  } = usePipedreamConnections()
  const [apps, setApps] = useState<PipedreamAppCatalogItem[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [catalogLoading, setCatalogLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [disconnectingId, setDisconnectingId] = useState<number | null>(null)
  // The slug of the connected app another account is being named for.
  const [addingAccountFor, setAddingAccountFor] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const searchRequestRef = useRef(0)
  // The banner renders `connectError ?? error`, so the hook's failure wins on a
  // tie — which silently masks anything this card reports afterwards. Clearing
  // the hook's as we set ours makes the newest error the visible one whichever
  // side raised it, instead of making one source permanently louder.
  //
  // Held in a ref because the loaders below need this, it needs the hook's
  // `clearError`, and the hook's `onSettled` closes over the loaders. The ref
  // breaks that cycle — the same reason `useSuggestionConnect` keeps its own
  // `onSettled` in one.
  const clearConnectErrorRef = useRef<() => void>(() => {})
  const showError = useCallback((message: string) => {
    clearConnectErrorRef.current()
    setError(message)
  }, [])

  // Options rather than trailing booleans: this reads `loadCatalog(search)` /
  // `{ append: true }` / `{ countAsSearch: false }` at the call sites, where
  // the positional form had grown to `loadCatalog(search, undefined, false,
  // true)` — two adjacent booleans whose meaning is only visible here.
  //
  // `countAsSearch` names the decision rather than its effect, and the decision
  // is what the caller knows: `pipedream_catalog_searched` measures a user
  // typing a product name, and this function also serves refreshes that are
  // nothing of the kind. Counting those would make search volume move whenever
  // connects move — including on abandonment, which the old hand-rolled
  // popup-close path never even reached.
  const loadCatalog = useCallback(async (
    query: string,
    { after, append = false, countAsSearch = true }: {
      after?: string | undefined
      append?: boolean
      countAsSearch?: boolean
    } = {},
  ) => {
    const requestId = ++searchRequestRef.current
    append ? setLoadingMore(true) : setCatalogLoading(true)
    try {
      const page = await pipedreamAppsService.listApps(
        query.trim() || undefined,
        after,
      )
      if (requestId !== searchRequestRef.current) return
      setApps((current) => append ? [...current, ...page.apps] : page.apps)
      setTotalCount(page.total_count)
      setNextCursor(page.next_cursor ?? null)
      setError(null)
      if (query.trim() && countAsSearch) {
        trackEvent(OnboardingEvents.PIPEDREAM_CATALOG_SEARCHED, {
          query: query.trim(),
          result_count: page.total_count,
        })
      }
    } catch (err) {
      if (requestId === searchRequestRef.current) {
        showError(getApiErrorMessage(err, 'Could not load the integration catalog.'))
      }
    } finally {
      if (requestId === searchRequestRef.current) {
        append ? setLoadingMore(false) : setCatalogLoading(false)
      }
    }
  }, [showError])

  // The whole connect flow, shared with the suggestion tiles. What the card
  // used to run by hand was missing three things the hook has: a watchdog for a
  // mobile redirect the browser accepts and then never performs (GET-11630,
  // which was still live on this surface), a settle for a popup the user simply
  // closes, and a count for an attempt abandoned by navigating away.
  //
  // The popup-close gap was the expensive one here: this card's poll re-read
  // the server and cleared the spinner but never called its own `finishConnect`,
  // so a connect the user completed and then dismissed the window on emitted no
  // success event at all. The hook settles that case as `closed` and asks the
  // question below.
  const {
    connectingKey,
    error: connectError,
    clearError,
    connect,
  } = useSuggestionConnect({
    surface: IntegrationSuggestionSurfaces.INTEGRATIONS_CATALOG,
    onSettled: async (suggestion) => {
      // Both reads, because the catalog's own `connected` flags go stale too.
      const [connected] = await Promise.all([
        loadConnections(),
        loadCatalog(search, { countAsSearch: false }),
      ])
      if (connected === null) return 'unknown'
      return pipedreamConnectLanded(
        connected,
        suggestion.catalog_key,
        suggestion.account_label,
      )
        ? 'connected'
        : 'not_connected'
    },
  })

  // Two error sources share one banner — the hook owns connect failures, this
  // card owns catalog/connection/disconnect ones — and the hook's takes
  // precedence. So every action that resets the banner has to reset both, or a
  // stale connect failure survives closing the modal and then hides the error
  // from whatever the user does next.
  clearConnectErrorRef.current = clearError

  const clearBanner = useCallback(() => {
    setError(null)
    clearError()
  }, [clearError])



  useEffect(() => {
    if (!open) return
    const timer = window.setTimeout(() => {
      void loadCatalog(search)
    }, search ? 250 : 0)
    return () => window.clearTimeout(timer)
  }, [open, search, loadCatalog])

  // Another card asked us to open, optionally pre-searched. Keyed on the nonce
  // so the same app requested twice still reopens.
  useEffect(() => {
    // Keep the access invariant inside this component, but deliberately leave
    // the flag out of the dependencies: a flag flap must not reopen a stale
    // request whose nonce did not change.
    if (!catalogBrowseEnabled || !openRequest) return
    setSearch(openRequest.query)
    clearBanner()
    setOpen(true)
  }, [openRequest?.nonce, openRequest?.query])

  const openCatalog = () => {
    trackEvent(OnboardingEvents.PIPEDREAM_CATALOG_OPENED)
    setOpen(true)
    clearBanner()
  }

  const connectApp = (app: PipedreamAppCatalogItem) => {
    // The click event moved into the hook (GET-13089), which is the only place
    // that knows whether the tap was accepted — the surface it is counted
    // against comes from this card's `useSuggestionConnect` options above.
    clearBanner()
    void connect(pipedreamAppSuggestion(app.app_slug, app.name, app.img_src ?? null))
  }

  const addAccount = (first: PipedreamAppConnectionResponse, label: string) => {
    setAddingAccountFor(null)
    clearBanner()
    void connect(pipedreamAppSuggestion(
      first.app_slug,
      first.name,
      first.img_src ?? null,
      label,
    ))
  }

  const disconnect = async (connection: PipedreamAppConnectionResponse) => {
    setDisconnectingId(connection.id)
    clearBanner()
    try {
      await disconnectRows([connection])
      await Promise.all([loadConnections(), open ? loadCatalog(search) : Promise.resolve()])
    } catch (err) {
      showError(getApiErrorMessage(
        err,
        `Could not disconnect ${pipedreamConnectionTitle(connection)}.`,
      ))
    } finally {
      setDisconnectingId(null)
    }
  }

  const disconnectButton = (
    connection: PipedreamAppConnectionResponse,
    className: string,
    iconClassName: string,
  ) => (
    <button
      type="button"
      onClick={() => void disconnect(connection)}
      disabled={disconnectingId === connection.id}
      className={className}
      aria-label={`Disconnect ${pipedreamConnectionTitle(connection)}`}
    >
      {disconnectingId === connection.id
        ? <Loader2 className={`${iconClassName} animate-spin`} />
        : <Unplug className={iconClassName} />}
    </button>
  )

  // Apps that already have a tile of their own in a category card above are
  // left out of this page's connection list: two rows for one connection means
  // two disconnect controls that behave differently (the tile drops every
  // account of the app, a row here drops one). The catalog modal below still
  // shows them all — in there the user is looking at the whole of Pipedream,
  // and hiding four apps from that view would just be wrong.
  const pageConnections = connections.filter(
    (connection) => !managedElsewhere.includes(connection.app_slug),
  )
  // One entry per app, however many accounts it holds.
  const pageApps = groupPipedreamAccounts(pageConnections)
  const accountsBySlug = new Map(
    groupPipedreamAccounts(connections).map(({ first, accounts }) => [first.app_slug, accounts]),
  )
  const addingAccounts = addingAccountFor === null
    ? []
    : accountsBySlug.get(addingAccountFor) ?? []

  return (
    <>
      {catalogBrowseEnabled ? (
        <div className="md:col-span-2 bg-gradient-to-br from-slate-950 to-slate-800 rounded-2xl p-6 text-white overflow-hidden relative">
          <div className="absolute -right-16 -top-20 w-64 h-64 rounded-full bg-primary-500/20 blur-3xl" />
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-5 justify-between">
            <div>
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center">
                  <Plug className="w-5 h-5 text-primary-300" />
                </div>
                <h3 className="text-lg font-semibold">Connect any app</h3>
              </div>
              <p className="text-sm text-slate-300 max-w-xl">
                Browse Pipedream&apos;s live catalog. Connect an account once and Catch
                can discover and use its available tools automatically.
              </p>
              {!connectionsLoading && pageApps.length > 0 && (
                <div className="flex items-center gap-2 mt-4">
                  <div className="flex -space-x-2">
                    {pageApps.slice(0, 5).map(({ first }) => (
                      <AppLogo
                        key={first.app_slug}
                        name={first.name}
                        src={first.img_src}
                        size="sm"
                      />
                    ))}
                  </div>
                  <span className="text-xs text-slate-300">
                    {pageApps.length} connected
                  </span>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={openCatalog}
              className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-white text-slate-900 text-sm font-semibold hover:bg-slate-100 transition-colors"
            >
              Browse integrations
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : pageConnections.length > 0 || (error ?? connectionsError) !== null ? (
        <div className="md:col-span-2 bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center shrink-0">
              <Plug className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Connected apps</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                Apps connected through Pipedream that Catch can use for you.
              </p>
            </div>
          </div>

          {(error ?? connectionsError) && (
            <div className="mt-4 flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
              <span>{error ?? connectionsError}</span>
              <button
                type="button"
                className="inline-flex shrink-0 items-center gap-1.5 font-semibold hover:text-red-900 disabled:opacity-60"
                disabled={connectionsLoading}
                onClick={() => void loadConnections()}
              >
                {connectionsLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {connectionsLoading ? 'Retrying…' : 'Try again'}
              </button>
            </div>
          )}

          {pageApps.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
              {pageApps.map(({ first, accounts }) => {
                // The first account alone reads as before. Once an app has a
                // second, each account is a row of its own with its name.
                const listed = accounts.length > 1 || !isDefaultPipedreamAccount(first)
                return (
                  <div
                    key={first.app_slug}
                    className="p-3 rounded-xl border border-gray-200"
                  >
                    <div className="flex items-center gap-3">
                      <AppLogo
                        name={first.name}
                        src={first.img_src}
                        size="sm"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {first.name}
                        </p>
                        <p className="text-xs text-green-700 mt-0.5">
                          {listed ? `${accounts.length} accounts connected` : 'Connected'}
                        </p>
                      </div>
                      {!listed && disconnectButton(
                        first,
                        'p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50',
                        'w-4 h-4',
                      )}
                    </div>
                    {listed && (
                      <ul className="mt-2 space-y-1 pl-11">
                        {accounts.map((account) => (
                          <li
                            key={account.id}
                            className="flex items-center justify-between gap-2"
                          >
                            <span className="text-xs text-gray-700 truncate">
                              {pipedreamAccountName(account)}
                            </span>
                            {disconnectButton(
                              account,
                              'p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50',
                              'w-3.5 h-3.5',
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                    {canAddPipedreamAccount(accounts) && (
                      <button
                        type="button"
                        onClick={() => setAddingAccountFor(first.app_slug)}
                        disabled={connectingKey !== null}
                        className="mt-2 ml-11 inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Add {first.name} account
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      ) : null}

      {catalogBrowseEnabled && open && (
        <div className="fixed inset-0 z-50 bg-black/55 p-3 sm:p-6 flex items-center justify-center">
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[min(820px,92vh)] flex flex-col overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pipedream-catalog-title"
          >
            <div className="px-5 sm:px-7 pt-6 pb-4 border-b border-gray-100">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <h2
                    id="pipedream-catalog-title"
                    className="text-xl font-semibold text-gray-900"
                  >
                    What should Catch connect to?
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {totalCount > 0
                      ? `${totalCount.toLocaleString()} apps with ready-to-use actions`
                      : 'Search the live Pipedream catalog'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  aria-label="Close integration catalog"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search Slack, Airtable, Linear, Stripe…"
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  autoFocus
                />
              </div>
              {connections.length > 0 && (
                <div className="flex gap-2 mt-4 overflow-x-auto pb-1">
                  {connections.map((connection) => (
                    <div
                      key={connection.id}
                      className="shrink-0 inline-flex items-center gap-2 pl-2 pr-1 py-1 rounded-full bg-green-50 border border-green-100 text-xs text-green-800"
                    >
                      <AppLogo name={connection.name} src={connection.img_src} size="sm" />
                      <span>{pipedreamConnectionTitle(connection)}</span>
                      {disconnectButton(
                        connection,
                        'p-1.5 rounded-full hover:bg-green-100 disabled:opacity-50',
                        'w-3.5 h-3.5',
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {(connectError ?? error ?? connectionsError) && (
              <div className="mx-5 sm:mx-7 mt-4 px-4 py-2.5 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
                {connectError ?? error ?? connectionsError}
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-5 sm:p-7">
              {catalogLoading ? (
                <div className="h-full flex items-center justify-center text-gray-500">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  Loading integrations…
                </div>
              ) : apps.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <Search className="w-9 h-9 text-gray-300 mb-3" />
                  <p className="font-medium text-gray-700">No matching integrations</p>
                  <p className="text-sm text-gray-500 mt-1">Try a product or company name.</p>
                  {onRequestIntegration && (
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false)
                        onRequestIntegration(search.trim())
                      }}
                      className="mt-4 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                    >
                      Ask us to add it
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {apps.map((app) => {
                      const accounts = accountsBySlug.get(app.app_slug) ?? []
                      const connected = accounts.length > 0 || app.connected
                      // A connected app that takes another account stays
                      // tappable, and the tap names the new account first.
                      const addable = canAddPipedreamAccount(accounts)
                      const connecting = connectingKey === app.app_slug
                      return (
                        <button
                          key={app.id}
                          type="button"
                          onClick={() => {
                            if (!connected) {
                              connectApp(app)
                            } else if (addable) {
                              setAddingAccountFor(app.app_slug)
                            }
                          }}
                          disabled={(connected && !addable) || connectingKey !== null}
                          className="text-left p-4 rounded-xl border border-gray-200 hover:border-primary-300 hover:shadow-sm transition-all disabled:cursor-default disabled:hover:shadow-none"
                        >
                          <div className="flex items-start gap-3">
                            <AppLogo name={app.name} src={app.img_src} />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-gray-900 truncate">
                                  {app.name}
                                </h3>
                                {connected && <Check className="w-4 h-4 text-green-600 shrink-0" />}
                              </div>
                              <p className="text-xs text-gray-500 mt-1 line-clamp-2 min-h-8">
                                {app.description || 'Connect this app to Catch'}
                              </p>
                              <span className={`inline-flex mt-3 text-xs font-semibold ${
                                connected ? 'text-green-700' : 'text-primary-600'
                              }`}>
                                {connecting ? (
                                  <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Connecting…</>
                                ) : connected
                                  ? (addable ? 'Connected · Add account' : 'Connected')
                                  : 'Connect'}
                              </span>
                            </div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                  {nextCursor && apps.length < totalCount && (
                    <div className="flex justify-center pt-6">
                      <button
                        type="button"
                        onClick={() => void loadCatalog(search, { after: nextCursor, append: true })}
                        disabled={loadingMore}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {loadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
                        Load more
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="px-5 sm:px-7 py-3 border-t border-gray-100 text-xs text-gray-400">
              Authentication and credentials are managed securely by Pipedream.
              Catch receives tools, not your password or API key.
            </div>
          </div>
        </div>
      )}

      {addingAccounts[0] && (
        <PipedreamAddAccountModal
          appName={addingAccounts[0].name}
          accounts={addingAccounts}
          onSubmit={(label) => addingAccounts[0] && addAccount(addingAccounts[0], label)}
          onClose={() => setAddingAccountFor(null)}
        />
      )}
    </>
  )
}
